/**
 * FeedFilterPanelContent — canonical feed-style filter panel (packages/ui)
 *
 * Part of the discovery domain (feed surface type).
 * Shared by Kloutforce Home and Katalog feed surfaces via thin wrappers.
 *
 * Generic over a Section key. Surface-specific extras (e.g. Katalog's
 * "Feed Focus" selector) are injected via the `extrasAbovePills` slot
 * to keep this component small and unbiased.
 */
import * as React from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  Building2,
  Tag,
} from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface TaxonomyOption {
  id: string;
  label: string;
  count?: number | null;
}

export interface FeedSectionOption<TSection extends string> {
  key: TSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface FeedFilterPanelContentProps<TSection extends string = string> {
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;

  /** Section toggle row (e.g. News/Pinned/Following or Active/Paused/Ended) */
  sectionOptions: FeedSectionOption<TSection>[];
  sectionTitle?: string;
  activeSections: TSection[];
  onToggleSection: (section: TSection) => void;

  /** Industry pills (2-col grid) */
  industryOptions?: TaxonomyOption[];
  activeIndustryIds?: string[];
  onToggleIndustry?: (id: string) => void;

  /** Categories — only shown when at least one industry is selected */
  categoryOptions?: TaxonomyOption[];
  activeCategoryIds?: string[];
  onToggleCategory?: (id: string) => void;

  /** Search input placeholder */
  searchPlaceholder?: string;

  /** Footer / CTA */
  onApply: () => void;
  onClear: () => void;
  onClearAll: () => void;
  hasCommittedFilters?: boolean;
  draftMatchesCommitted?: boolean;

  /**
   * Slot for surface-specific extras (e.g. Katalag's Feed Focus selector).
   * Rendered above the section toggles. Optional.
   */
  extrasAbovePills?: React.ReactNode;

  /**
   * Whether the surface considers itself to "have changes" beyond what this
   * component can detect (e.g. Katalog's focus !== 'katalog'). OR'd with
   * the internally computed hasChanges to control the Clear All disabled state.
   */
  extrasHasChanges?: boolean;
}

const COLLAPSED_LIMIT = 8;
const EXPANDED_LIMIT = 20;

export function FeedFilterPanelContent<TSection extends string>({
  searchQuery,
  onSearchQueryChange,
  sectionOptions,
  sectionTitle = 'Feed Sections',
  activeSections,
  onToggleSection,
  industryOptions = [],
  activeIndustryIds = [],
  onToggleIndustry,
  categoryOptions = [],
  activeCategoryIds = [],
  onToggleCategory,
  searchPlaceholder = 'Search your feed...',
  onApply,
  onClear,
  onClearAll,
  hasCommittedFilters = false,
  draftMatchesCommitted = false,
  extrasAbovePills,
  extrasHasChanges = false,
}: FeedFilterPanelContentProps<TSection>) {
  const [industriesExpanded, setIndustriesExpanded] = React.useState(false);

  const hasChanges =
    extrasHasChanges ||
    activeSections.length > 0 ||
    searchQuery.trim().length > 0 ||
    activeIndustryIds.length > 0 ||
    activeCategoryIds.length > 0;

  const selectedIndustrySet = React.useMemo(
    () => new Set(activeIndustryIds),
    [activeIndustryIds],
  );

  const visibleIndustries = React.useMemo(() => {
    const sorted = [...industryOptions].sort((a, b) => {
      const aSelected = selectedIndustrySet.has(a.id) ? 1 : 0;
      const bSelected = selectedIndustrySet.has(b.id) ? 1 : 0;
      if (aSelected !== bSelected) return bSelected - aSelected;
      return a.label.localeCompare(b.label);
    });
    return sorted.slice(0, industriesExpanded ? EXPANDED_LIMIT : COLLAPSED_LIMIT);
  }, [industryOptions, selectedIndustrySet, industriesExpanded]);

  const hasMoreIndustries = industryOptions.length > COLLAPSED_LIMIT;
  const showCategories = activeIndustryIds.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchQueryChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Surface-specific extras (e.g. Katalag Feed Focus) */}
        {extrasAbovePills}

        {/* Section toggles */}
        <FilterSection title={sectionTitle}>
          <div className="flex gap-2">
            {sectionOptions.map((option) => {
              const isActive = activeSections.includes(option.key);
              const Icon = option.icon;
              return (
                <button
                  key={option.key}
                  onClick={() => onToggleSection(option.key)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors select-none',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground border border-border hover:bg-muted',
                  )}
                >
                  {isActive && <Check className="w-3 h-3 shrink-0" />}
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </FilterSection>

        {/* Industries — 2-column grid pills */}
        {industryOptions.length > 0 && (
          <FilterSection title="Industries" icon={<Building2 className="w-3.5 h-3.5" />}>
            <div className="grid grid-cols-2 gap-1.5">
              {visibleIndustries.map((item) => {
                const isSelected = selectedIndustrySet.has(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => onToggleIndustry?.(item.id)}
                    className={cn(
                      'h-9 px-2.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 min-w-0 select-none',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-foreground border border-border hover:bg-muted',
                    )}
                  >
                    {isSelected && <Check className="w-3 h-3 shrink-0" />}
                    <span className="truncate flex-1 text-left">{item.label}</span>
                    {item.count != null && (
                      <span className="opacity-60 tabular-nums shrink-0 text-[10px]">
                        ({item.count})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {hasMoreIndustries && (
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => setIndustriesExpanded(!industriesExpanded)}
                  className="px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-1"
                >
                  {industriesExpanded ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                  {industriesExpanded ? 'less' : `more (${industryOptions.length})`}
                </button>
              </div>
            )}
          </FilterSection>
        )}

        {/* Categories — full-width rows */}
        {showCategories && (
          <FilterSection title="Categories" icon={<Tag className="w-3.5 h-3.5" />}>
            {categoryOptions.length > 0 ? (
              <div className="space-y-0.5">
                {categoryOptions.map((cat) => {
                  const isSelected = activeCategoryIds.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => onToggleCategory?.(cat.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors select-none',
                        isSelected
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-foreground hover:bg-muted',
                      )}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {isSelected && (
                          <Check className="w-3.5 h-3.5 shrink-0 text-primary" />
                        )}
                        <span className="truncate">{cat.label}</span>
                      </span>
                      {cat.count != null && (
                        <span className="text-muted-foreground tabular-nums text-xs shrink-0 ml-2">
                          ({cat.count})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">
                No categories in selected industries
              </p>
            )}
          </FilterSection>
        )}
      </div>

      {/* Footer: Apply + Reset */}
      <div className="shrink-0 border-t border-border px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={onClearAll}
            className="text-xs font-medium text-destructive hover:text-destructive/80"
            disabled={!hasChanges}
          >
            Clear All
          </button>
          <button
            onClick={onClear}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            Reset
          </button>
        </div>
        <button
          data-panel-apply="true"
          onClick={onApply}
          disabled={draftMatchesCommitted}
          className={cn(
            'w-full py-2.5 rounded-full text-sm font-semibold transition-colors',
            draftMatchesCommitted
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/90',
          )}
        >
          {draftMatchesCommitted
            ? 'Filters applied'
            : hasCommittedFilters
              ? 'Update filters'
              : 'Apply filters'}
        </button>
      </div>
    </div>
  );
}

/* ===== Helper ===== */

export const FilterSection: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div className="bg-card rounded-xl border border-border p-3">
    <h3 className="text-xs font-semibold text-foreground mb-2.5 flex items-center gap-1.5">
      {icon}
      {title}
    </h3>
    {children}
  </div>
);

export default FeedFilterPanelContent;